import React, { useState, useCallback, useEffect } from 'react';
import { History } from 'lucide-react';
import { statusLabel, statusStyle, formatDate } from './reimbursementStatus';
import { SECTION_HEADING } from './FormShell';

// Paginated, most-recent-first list of the member's Reimbursements. `excludeIds` hides the ones
// already shown as cards above. `renderDetails` is the main line, `renderMeta` an optional second one.
const ReimbursementHistory = ({ title, apiFn, refreshKey, renderDetails, renderMeta, excludeIds = [] }) => {
    const [records, setRecords] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const loadPage = useCallback(async (nextPage) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await apiFn(nextPage);
            const data = response.data?.data || {};
            const newRecords = Array.isArray(data.records) ? data.records : [];
            setRecords((prev) => (nextPage === 1 ? newRecords : [...prev, ...newRecords]));
            setHasMore(Boolean(data.hasMore));
            setPage(nextPage);
        } catch (err) {
            setError('Failed to load history. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [apiFn]);

    useEffect(() => {
        loadPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiFn, refreshKey]);

    const shown = records.filter((record) => !excludeIds.includes(record._id));

    return (
        <section className="mb-2" data-testid="reimbursement-history">
            <div className="flex items-center gap-2 mb-2.5">
                <History className="w-4 h-4 text-portal-sub" aria-hidden="true" />
                <p className={SECTION_HEADING}>{title}</p>
            </div>

            {isLoading && records.length === 0 && !error && (
                <div className="bg-white border border-portal-border rounded-xl p-3 animate-pulse">
                    <div className="h-3.5 bg-portal-bg rounded w-1/2 mb-2" />
                    <div className="h-3 bg-portal-bg rounded w-3/4" />
                </div>
            )}

            {error && (
                <p role="alert" className="text-red-700 text-xs mb-2">
                    {error} <button type="button" className="underline font-semibold" onClick={() => loadPage(1)}>Retry</button>
                </p>
            )}

            {!isLoading && shown.length === 0 && !error && (
                <p className="text-xs text-portal-sub">No past reimbursements.</p>
            )}

            {shown.map((record) => {
                const meta = renderMeta?.(record);
                return (
                    <div key={record._id} className="bg-white border border-portal-border rounded-xl p-3 mb-2">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-portal-ink">{renderDetails(record)}</p>
                                <p className="text-xs text-portal-sub">{[meta, formatDate(record.createdAt)].filter(Boolean).join(' · ')}</p>
                            </div>
                            <span className={`flex-shrink-0 max-w-[55%] text-[11px] font-semibold px-2 py-0.5 rounded-full border text-right ${statusStyle(record.reimbursement_status)}`}>
                                {statusLabel(record.reimbursement_status)}
                            </span>
                        </div>
                    </div>
                );
            })}

            {hasMore && (
                <button
                    type="button"
                    onClick={() => loadPage(page + 1)}
                    disabled={isLoading}
                    className="w-full py-2 text-xs font-semibold text-portal-purple-dark hover:underline disabled:opacity-50"
                >
                    {isLoading ? 'Loading…' : 'Load more'}
                </button>
            )}
        </section>
    );
};

export default ReimbursementHistory;
